import PublicApiProvider from '../../../../core/api/PublicApiProvider';
import { CaseStatusData } from '../types/CaseStatusData';
import { ContactInfo } from '../types/ContactInfo';
import { PaymentProvider, PaymentRedirect, PaymentResult } from '../types/PaymentTypes';

export class CaseStatusTokenError extends Error {
  constructor() {
    super('Invalid or missing case access token');
    this.name = 'CaseStatusTokenError';
  }
}

export class CaseStatusPaymentError extends Error {
  constructor() {
    super('Payment initiation failed');
    this.name = 'CaseStatusPaymentError';
  }
}

const translationsCache = new Map<string, Record<string, string>>();
const paymentTranslationsCache = new Map<string, Record<string, string>>();

export const CaseStatusApi = {
  getTranslations: async (
    culture: string,
    accessToken: string
  ): Promise<Record<string, string>> => {
    if (translationsCache.has(culture)) {
      return translationsCache.get(culture)!;
    }
    try {
      const query = new URLSearchParams({ culture });
      const result = await PublicApiProvider.get<Record<string, string>>(
        `api/v1/case/translations?${query}`,
        undefined,
        accessToken
      );
      translationsCache.set(culture, result);
      return result;
    } catch (error: any) {
      if (error?.response?.status === 401) {
        throw new CaseStatusTokenError();
      }
      return {};
    }
  },

  getPaymentTranslations: async (
    culture: string,
    caseNumber: string,
    transactionId: string,
    paymentToken: string
  ): Promise<Record<string, string>> => {
    if (paymentTranslationsCache.has(culture)) {
      return paymentTranslationsCache.get(culture)!;
    }
    try {
      const query = new URLSearchParams({ culture, caseNumber, transactionId });
      const result = await PublicApiProvider.get<Record<string, string>>(
        `api/v1/case/payment-translations?${query}`,
        undefined,
        undefined,
        paymentToken
      );
      paymentTranslationsCache.set(culture, result);
      return result;
    } catch (error: any) {
      if (error?.response?.status === 401) {
        throw new CaseStatusTokenError();
      }
      return {};
    }
  },

  getAccessToken: async (
    caseNumber: string,
    turnstileToken?: string,
    zipCode?: string
  ): Promise<string> => {
    const data = await PublicApiProvider.post<{ accessToken: string }>(
      'api/v1/case/access-token',
      { caseNumber, ...(zipCode ? { zipCode } : {}) },
      turnstileToken
    );
    return data.accessToken;
  },

  getCaseStatus: async (caseNumber: string, accessToken: string): Promise<CaseStatusData | null> => {
    try {
      const data = await PublicApiProvider.get<CaseStatusData>(
        `api/v1/case/status?caseNumber=${encodeURIComponent(caseNumber)}`,
        undefined,
        accessToken
      );

      return data;
    } catch (error: any) {
      const status = error?.response?.status;
      if (status === 401) {
        throw new CaseStatusTokenError();
      }
      return null;
    }
  },

  getContactInfo: (() => {
    const cache = new Map<string, ContactInfo>();
    return async (accessToken: string): Promise<ContactInfo | null> => {
      if (cache.has(accessToken)) {
        return cache.get(accessToken)!;
      }
      try {
        const result = await PublicApiProvider.get<ContactInfo>('api/v1/case/contact', undefined, accessToken);
        cache.set(accessToken, result);
        return result;
      } catch (error: any) {
        if (error?.response?.status === 401) {
          throw new CaseStatusTokenError();
        }
        return null;
      }
    };
  })(),

  getPaymentProviders: async (accessToken: string): Promise<PaymentProvider[]> => {
    try {
      return await PublicApiProvider.get<PaymentProvider[]>('api/v1/payment/providers', undefined, accessToken);
    } catch (error: any) {
      if (error?.response?.status === 401) throw new CaseStatusTokenError();
      return [];
    }
  },

  initiatePayment: async (
    caseNumber: string,
    providerType: number,
    accessToken: string
  ): Promise<PaymentRedirect> => {
    try {
      return await PublicApiProvider.post<PaymentRedirect>(
        `api/v1/payment/paynow?caseNumber=${encodeURIComponent(caseNumber)}`,
        { providerType },
        undefined,
        accessToken
      );
    } catch (error: any) {
      const status = error?.response?.status;
      if (status === 401) throw new CaseStatusTokenError();
      throw new CaseStatusPaymentError();
    }
  },

  getPaymentResult: async (
    caseNumber: string,
    transactionId: string,
    responseCode: string,
    paymentItemType: string,
    paymentToken: string,
    providerType: number
  ): Promise<PaymentResult> => {
    try {
      return await PublicApiProvider.postWithPaymentToken<PaymentResult>(
        `api/v1/payment/return?caseNumber=${encodeURIComponent(caseNumber)}&transactionId=${encodeURIComponent(transactionId)}`,
        { responseCode, paymentItemType, providerType },
        paymentToken
      );
    } catch (error: any) {
      const status = error?.response?.status;
      if (status === 401) throw new CaseStatusTokenError();
      throw new CaseStatusPaymentError();
    }
  },
};
